package com.mps.asc_app.data.bookings

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@OptIn(ExperimentalTime::class)
class BookingRepositoryImpl : BookingsRepository {
    private val db = Firebase.firestore

    private fun parseKind(snapshot: DocumentSnapshot): BookingKind {
        val raw = snapshot.get<String?>("kind")?.trim()?.lowercase()
        return when (raw) {
            "blocked" -> BookingKind.BLOCKED
            else -> BookingKind.BOOKING
        }
    }

    private fun readOwnerUserId(snapshot: DocumentSnapshot): String? {
        return snapshot.get<String?>("ownerUserId")
            ?: snapshot.get<String?>("userId")
    }

    private fun parseScope(snapshot: DocumentSnapshot): BookingScope {
        val raw = snapshot.get<String?>("bookingScope")?.trim()?.lowercase()
        return when (raw) {
            "training" -> BookingScope.TRAINING
            else -> BookingScope.SELF
        }
    }

    private suspend fun loadUserDisplayName(userId: String): String? {
        if (userId.isBlank() || userId == "null") return null

        // Defensive: je nach Firestore Rules dürfen normale Nutzer evtl. nicht alle user-Dokumente lesen.
        // In dem Fall darf die Buchungsanzeige NICHT kaputtgehen.
        val userDoc = runCatching { db.collection("users").document(userId).get() }
            .getOrNull()
            ?: return null
        val firstName = userDoc.get<String?>("firstName")?.trim().orEmpty()
        val lastName = userDoc.get<String?>("lastName")?.trim().orEmpty()
        val fullName = listOf(firstName, lastName)
            .filter { it.isNotBlank() }
            .joinToString(" ")

        if (fullName.isNotBlank()) return fullName

        val fallbackName = userDoc.get<String?>("displayName")?.trim().orEmpty()
        return fallbackName.ifBlank { null }
    }

    private suspend fun loadUserPrivacyConsentAccepted(userId: String): Boolean? {
        if (userId.isBlank() || userId == "null") return null

        val userDoc = runCatching { db.collection("users").document(userId).get() }
            .getOrNull()
            ?: return null

        return userDoc.get<Boolean?>("privacyConsentAccepted")
    }

    private suspend fun loadUserDisplayNameSafely(userId: String): String? {
        if (userId.isBlank() || userId == "null") return null
        // Lesbarkeit haengt von Firestore Rules ab (Admin: i.d.R. erlaubt, normale Nutzer: oft verboten).
        // Deshalb immer versuchen und bei Permission-Denied still auf null fallen.
        return runCatching { loadUserDisplayName(userId) }
            .getOrNull()
    }

    private suspend fun loadUserPrivacyConsentAcceptedSafely(userId: String): Boolean? {
        if (userId.isBlank() || userId == "null") return null
        // Lesbarkeit haengt von Firestore Rules ab (Admin: i.d.R. erlaubt, normale Nutzer: oft verboten).
        // Deshalb immer versuchen und bei Permission-Denied still auf null fallen.
        return runCatching { loadUserPrivacyConsentAccepted(userId) }
            .getOrNull()
    }

    private fun readBookingTimestamp(snapshot: DocumentSnapshot): Timestamp? {
        return snapshot.get<Timestamp?>("slotTs")
            ?: snapshot.get<Timestamp?>("timestamp")
    }

    private suspend fun resolveBookingPlayers(snapshot: DocumentSnapshot): List<BookingPlayer> {
        if (parseKind(snapshot) == BookingKind.BLOCKED) {
            return emptyList()
        }

        val storedPlayers = snapshot.get<List<BookingPlayer>?>("bookingplayers")
            ?: snapshot.get<List<BookingPlayer>?>("players")

        if (!storedPlayers.isNullOrEmpty()) {
            return storedPlayers.map { player ->
                val storedName = player.displayNameSnapshot?.trim().orEmpty()
                val resolvedName = if (storedName.isNotBlank()) {
                    storedName
                } else {
                    loadUserDisplayNameSafely(player.userId) ?: "Belegt"
                }
                // Legacy-Fallback: Wenn bereits ein Name-Snapshot im Booking steht,
                // behandeln wir ihn als freigegeben, auch wenn der Consent-Snapshot fehlt.
                // So bleiben alte Buchungen fuer Nicht-Admins sichtbar, ohne users/{uid} lesen zu muessen.
                val resolvedConsent = player.privacyConsentAcceptedSnapshot
                    ?: if (storedName.isNotBlank()) true else loadUserPrivacyConsentAcceptedSafely(player.userId)
                player.copy(
                    displayNameSnapshot = resolvedName,
                    privacyConsentAcceptedSnapshot = resolvedConsent,
                )
            }
        }

        val ownerUserId = snapshot.get<String?>("ownerUserId")
            ?: snapshot.get<String?>("userId")

        val playerIds = snapshot.get<List<String>?>("playerIds")
            ?: ownerUserId?.let { listOf(it) }
            ?: emptyList()

        val storedPlayerNames = snapshot.get<List<String>?>("playernames")
            ?: snapshot.get<List<String>?>("playerNames")
            ?: emptyList()

        return playerIds.mapIndexed { index, userId ->
            val storedName = storedPlayerNames.getOrNull(index)?.trim().orEmpty()
            val resolvedName = if (storedName.isNotBlank()) {
                storedName
            } else {
                loadUserDisplayNameSafely(userId) ?: "Belegt"
            }
            val resolvedConsent = if (storedName.isNotBlank()) {
                true
            } else {
                loadUserPrivacyConsentAcceptedSafely(userId)
            }

            BookingPlayer(
                userId = userId,
                displayNameSnapshot = resolvedName,
                privacyConsentAcceptedSnapshot = resolvedConsent,
            )
        }
    }

    private fun epochMillisToTimestamp(epochMillis: Long): Timestamp {
        val seconds = epochMillis / 1000
        val nanos = ((epochMillis % 1000) * 1_000_000).toInt()
        return Timestamp(seconds = seconds, nanoseconds = nanos)
    }

    private fun instantToTimestamp(instant: Instant): Timestamp {
        return Timestamp(
            seconds = instant.epochSeconds,
            nanoseconds = instant.nanosecondsOfSecond
        )
    }

    private fun timestampToInstant(ts: Timestamp): Instant {
        val epochMillis = ts.seconds * 1000 + ts.nanoseconds / 1_000_000
        return Instant.fromEpochMilliseconds(epochMillis)
    }

    override suspend fun countFutureBookingsForUser(
        userId: String,
        nowEpochMillis: Long
    ): Int {
        if (userId.isBlank()) return 0

        val ownerSnap = db.collection("bookings")
            .where { "ownerUserId" equalTo userId }
            .get()

        val legacySnap = db.collection("bookings")
            .where { "userId" equalTo userId }
            .get()

        return (ownerSnap.documents + legacySnap.documents)
            .groupBy { it.id }
            .values
            .mapNotNull { docsForId -> docsForId.firstOrNull() }
            .count { doc ->
                if (parseKind(doc) == BookingKind.BLOCKED) return@count false
                if (parseScope(doc) != BookingScope.SELF) return@count false
                val ts = readBookingTimestamp(doc) ?: return@count false
                val epochMillis = ts.seconds * 1000 + ts.nanoseconds / 1_000_000
                epochMillis >= nowEpochMillis
            }
    }

    override suspend fun getFutureBookingsForUser(
        userId: String,
        userEmail: String?,
        nowEpochMillis: Long,
        limit: Int,
    ): List<Bookings> {
        if (userId.isBlank()) return emptyList()

        // Robust & index-freundlich:
        // Viele Bestandsdaten nutzen teils unterschiedliche Timestamp-Felder (slotTs/timestamp).
        // Zudem erfordert ein >= Filter oft einen Composite Index.
        // Daher laden wir alle Buchungen des Users und filtern clientseitig nach Datum.
        val ownerSnap = db.collection("bookings")
            .where { "ownerUserId" equalTo userId }
            .get()

        val legacySnap = db.collection("bookings")
            .where { "userId" equalTo userId }
            .get()

        // Optionaler Bestands-Fallback (userEmail). Kann je nach Rules PERMISSION_DENIED auslösen.
        // Daher defensiv: bei Fehler ignorieren.
        val emailSnap = userEmail
            ?.trim()
            ?.takeIf { it.isNotBlank() }
            ?.let { email ->
                runCatching {
                    db.collection("bookings")
                        .where { "userEmail" equalTo email }
                        .get()
                }.getOrNull()
            }

        val docs = buildList {
            addAll(ownerSnap.documents)
            addAll(legacySnap.documents)
            if (emailSnap != null) addAll(emailSnap.documents)
        }

        return docs
            .groupBy { it.id }
            .values
            .mapNotNull { it.firstOrNull() }
            .mapNotNull { doc ->
                val kind = parseKind(doc)
                if (kind == BookingKind.BLOCKED) return@mapNotNull null

                val ts = readBookingTimestamp(doc) ?: return@mapNotNull null
                val epochMillis = ts.seconds * 1000 + ts.nanoseconds / 1_000_000
                if (epochMillis < nowEpochMillis) return@mapNotNull null
                val slotsInstant = timestampToInstant(ts)
                val bookingplayers = resolveBookingPlayers(doc)
                val courtId = doc.get<String?>("courtId") ?: ""

                Bookings(
                    id = doc.id,
                    courtId = courtId,
                    slotsTs = slotsInstant,
                    kind = kind,
                    blockedComment = null,
                    ownerUserId = readOwnerUserId(doc),
                    bookingScope = parseScope(doc),
                    trainingGroupId = doc.get<String?>("trainingGroupId")?.trim()?.takeIf { it.isNotBlank() },
                    bookingplayers = bookingplayers,
                    maxPlayers = doc.get<Int?>("maxPlayers") ?: 4,
                    minPlayers = doc.get<Int?>("minPlayers") ?: 2,
                )
            }
            .sortedBy { it.slotsTs }
            .take(limit.coerceIn(0, 50))
    }

    override suspend fun createBooking(
        bookingId: String,
        courtId: String,
        slotEpochMillis: Long,
        userId: String,
        displayNameSnapshot: String?,
        bookingScope: BookingScope,
        trainingGroupId: String?,
    ): Boolean {
        if (bookingId.isBlank() || courtId.isBlank() || userId.isBlank()) return false

        val docRef = db.collection("bookings").document(bookingId)
        val existing = docRef.get()
        val existingSlot = readBookingTimestamp(existing)
        if (existingSlot != null) return false

        val safeDisplayName = if (bookingScope == BookingScope.TRAINING) {
            "Training"
        } else {
            loadUserDisplayNameSafely(userId)
                ?: displayNameSnapshot?.trim().orEmpty().ifBlank { "Belegt" }
        }
        val privacyConsentAccepted = loadUserPrivacyConsentAcceptedSafely(userId) == true

        docRef.set(
            mapOf(
                "kind" to "booking",
                "courtId" to courtId,
                "slotTs" to epochMillisToTimestamp(slotEpochMillis),
                "ownerUserId" to userId,
                "userId" to userId,
                // Bestands-Kompatibilität: ältere Dokumente nutzen teils userEmail zur Zuordnung
                "userEmail" to (Firebase.auth.currentUser?.email ?: ""),
                "bookingScope" to when (bookingScope) {
                    BookingScope.SELF -> "self"
                    BookingScope.TRAINING -> "training"
                },
                "trainingGroupId" to trainingGroupId?.trim().orEmpty(),
                "playerIds" to listOf(userId),
                "bookingplayers" to listOf(
                    BookingPlayer(
                        userId = userId,
                        displayNameSnapshot = safeDisplayName,
                        privacyConsentAcceptedSnapshot = privacyConsentAccepted,
                    )
                ),
                "maxPlayers" to 4,
                "minPlayers" to 2,
            )
        )
        return true
    }

    override suspend fun blockSlot(
        bookingId: String,
        courtId: String,
        slotEpochMillis: Long,
        adminUserId: String,
        comment: String,
        trainingGroupId: String?,
        recurringRule: String?,
    ): Boolean {
        if (bookingId.isBlank() || courtId.isBlank() || adminUserId.isBlank()) return false

        val docRef = db.collection("bookings").document(bookingId)
        val existing = docRef.get()
        val existingSlot = readBookingTimestamp(existing)
        if (existingSlot != null) return false

        val cleanedComment = comment.trim()

        docRef.set(
            mapOf(
                "kind" to "blocked",
                "courtId" to courtId,
                "slotTs" to epochMillisToTimestamp(slotEpochMillis),
                "blockedByUserId" to adminUserId,
                "blockedComment" to cleanedComment,
                "bookingScope" to "training",
                "trainingGroupId" to trainingGroupId?.trim().orEmpty(),
                "recurringRule" to recurringRule?.trim().orEmpty(),
                "maxPlayers" to 0,
                "minPlayers" to 0,
            )
        )
        return true
    }

    override suspend fun blockRecurringSlots(
        entries: List<RecurringBlockEntry>,
        actorUserId: String,
        comment: String,
        trainingGroupId: String?,
    ): Int {
        if (entries.isEmpty() || actorUserId.isBlank()) return 0
        var created = 0
        entries.forEach { entry ->
            val ok = blockSlot(
                bookingId = entry.bookingId,
                courtId = entry.courtId,
                slotEpochMillis = entry.slotEpochMillis,
                adminUserId = actorUserId,
                comment = comment,
                trainingGroupId = trainingGroupId,
                recurringRule = entry.recurringRule,
            )
            if (ok) created += 1
        }
        return created
    }

    override suspend fun unblockSlot(bookingId: String): Boolean {
        if (bookingId.isBlank()) return false
        val docRef = db.collection("bookings").document(bookingId)
        val snap = docRef.get()
        if (!snap.exists) return false
        if (parseKind(snap) != BookingKind.BLOCKED) return false

        docRef.delete()
        return true
    }

    override suspend fun cancelBooking(
        bookingId: String,
        userId: String
    ): Boolean {
        val docRef = db.collection("bookings").document(bookingId)
        val existing = docRef.get()
        if (parseKind(existing) == BookingKind.BLOCKED) return false
        val ownerId = existing.get<String?>("ownerUserId") ?: existing.get<String?>("userId")
        if (ownerId.isNullOrBlank() || ownerId != userId) return false

        docRef.delete()
        return true
    }

    override suspend fun getBookingsForTimeslot(
        id: String,
        startEpochMillis: Long
    ): Bookings? {
        val snap = db.collection("bookings").document(id).get()

        val courtId = snap.get<String?>("courtId") ?: ""
        val ts = readBookingTimestamp(snap) ?: return null
        val slotsTs = timestampToInstant(ts)
        val kind = parseKind(snap)
        val blockedComment = snap.get<String?>("blockedComment")?.trim()
            ?: snap.get<String?>("comment")?.trim()
        val bookingplayers = resolveBookingPlayers(snap)
        val maxPlayers = snap.get<Int?>("maxPlayers") ?: 4
        val minPlayers = snap.get<Int?>("minPlayers") ?: 2

        return Bookings(
            id = id,
            courtId = courtId,
            slotsTs = slotsTs,
            kind = kind,
            blockedComment = blockedComment,
            ownerUserId = readOwnerUserId(snap),
            bookingScope = parseScope(snap),
            trainingGroupId = snap.get<String?>("trainingGroupId")?.trim()?.takeIf { it.isNotBlank() },
            bookingplayers = bookingplayers,
            maxPlayers = maxPlayers,
            minPlayers = minPlayers
        )
    }

    override suspend fun getBookingsForDay(
        courtId: String,
        dayStartEpochMillis: Long,
        dayEndEpochMillis: Long
    ): List<Bookings> {
        val startTs = epochMillisToTimestamp(dayStartEpochMillis)
        val endTs = epochMillisToTimestamp(dayEndEpochMillis)

        val snap = db.collection("bookings")
            .where { "courtId" equalTo courtId }
            .where { "slotTs" greaterThanOrEqualTo startTs }
            .where { "slotTs" lessThan endTs }
            .get()

        return snap.documents.mapNotNull { doc ->
            val id = doc.id
            val cId = doc.get<String?>("courtId") ?: return@mapNotNull null

            val slotTs = readBookingTimestamp(doc) ?: return@mapNotNull null
            val slotsInstant = timestampToInstant(slotTs)
            val kind = parseKind(doc)
            val bookingplayers = resolveBookingPlayers(doc)
            val maxPlayers = doc.get<Int?>("maxPlayers") ?: 4
            val minPlayers = doc.get<Int?>("minPlayers") ?: 2
            val blockedComment = doc.get<String?>("blockedComment")?.trim()
                ?: doc.get<String?>("comment")?.trim()

            Bookings(
                id = id,
                courtId = cId,
                slotsTs = slotsInstant,
                kind = kind,
                blockedComment = blockedComment,
                ownerUserId = readOwnerUserId(doc),
                bookingScope = parseScope(doc),
                trainingGroupId = doc.get<String?>("trainingGroupId")?.trim()?.takeIf { it.isNotBlank() },
                bookingplayers = bookingplayers,
                maxPlayers = maxPlayers,
                minPlayers = minPlayers
            )
        }
    }
}